import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import { apiService } from "../services/api";
import { MessageForm } from "../components/message/MessageForm";
import { MessageFormData } from "../schemas/message";
import { ScheduleInput } from "../components/message/ScheduleInput";
import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import { ZodError } from "zod";
import axios, { CancelTokenSource } from "axios";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

export const CreateMessage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Add ref to track component mounted state
  const isMounted = useRef(true);
  // Reference for axios cancel token
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const [schedule, setSchedule] = useState<Date | undefined>();
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Set up cleanup for component unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // Cancel any pending requests
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  // Safe state setter that only updates if component is mounted
  const safeSetUploadProgress = (progress: number) => {
    if (isMounted.current) {
      setUploadProgress(progress);
    }
  };

  const safeSetIsUploadingMedia = (isUploading: boolean) => {
    if (isMounted.current) {
      setIsUploadingMedia(isUploading);
    }
  };

  // Enhanced error handling for file operations
  const handleFileOperation = async (operation: () => Promise<any>) => {
    try {
      return await operation();
    } catch (error) {
      console.error("File operation error:", error);
      
      // Handle specific DOM-related errors
      if (error instanceof DOMException) {
        console.warn("DOM Exception during file operation:", error.message);
      }
      
      if (isMounted.current) {
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: t("message.file_processing_error"),
        });
      }
      throw error;
    }
  };

  // Handle media upload for both create and send mutations
  const handleMediaUpload = async (data: MessageFormData) => {
    if (data.content.type !== "text" && data.content[data.content.type]?.link) {
      safeSetIsUploadingMedia(true);
      safeSetUploadProgress(0);
      
      // Create a new cancel token for this upload
      cancelTokenRef.current = axios.CancelToken.source();
      
      try {
        // Wrap fetch in error handler for file operations
        const mediaFile = await handleFileOperation(async () => {
          const response = await fetch(data.content[data.content.type]!.link);
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
          }
          return response.blob();
        });
        
        if (!isMounted.current) return data;
        
        const filename = data.content[data.content.type]?.filename || "media";
        
        // Create File safely with error handling
        const file = await handleFileOperation(async () => {
          return new File([mediaFile], filename, { type: mediaFile.type });
        });

        const uploadUrlResp = (await apiService.generateUploadUrl(filename))
          .data;

        try {
          const uploadResult = await axios.put(uploadUrlResp.url, file, {
            cancelToken: cancelTokenRef.current.token,
            onUploadProgress: (progressEvent) => {
              if (!isMounted.current) return;
              
              if (progressEvent.total) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                if (uploadProgress == 0 && percentCompleted > 99) {
                  // If file is very small, progress might jump from 0% to 100%
                  // To prevent feeling that file disappeared, keep 0 until we get a larger value
                  return;
                } else if (percentCompleted < uploadProgress) {
                  // Keep the highest value we received
                  return;
                } else {
                  safeSetUploadProgress(percentCompleted);
                }
              } else {
                // If total is not defined, better to show "uploading..." than wrong value
                safeSetUploadProgress(0);
              }
            },
          });
          console.log("File uploaded successfully:", uploadResult);
          if (uploadResult.status !== 200) {
            throw new Error("Failed to upload media");
          }
        } catch (error) {
          // Check if the error is due to component unmounting
          if (axios.isCancel(error)) {
            console.log('Upload cancelled due to component unmount');
            return data;
          }
          
          console.error("Upload failed:", error);
          if (isMounted.current) {
            safeSetUploadProgress(0);
          }
        }

        if (isMounted.current) {
          const fileUploaded = await apiService.createMediaFromUrl(
            uploadUrlResp.key,
            filename,
            mediaFile.type
          );

          // Update the media object with the server file ID
          data.content[data.content.type]!.id = fileUploaded?.data?.id;
          delete data.content[data.content.type]?.link;
        }
      } finally {
        if (isMounted.current) {
          safeSetIsUploadingMedia(false);
        }
        // Clear the cancel token reference
        cancelTokenRef.current = null;
      }
    }
    return data;
  };

  const createMessageMutation = useMutation({
    mutationKey: ["createMessage"],
    mutationFn: async (data: MessageFormData) => {
      data = await handleMediaUpload(data);
      if (schedule) {
        data.schedule = schedule.toISOString();
      } else {
        data.schedule = undefined;
      }
      return apiService.createMessage(data);
    },
    onSuccess: () => {
      // invalidate the messages query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["messages"] });

      toast({
        title: t("common.success"),
        description: schedule
          ? t("message.scheduled_successfully")
          : t("message.created_successfully"),
      });
      navigate("/dashboard");
    },
    onError: (error) => {
      if (error instanceof ZodError) {
        toast({
          variant: "destructive",
          title: t("common.validation_error"),
          description: error.errors.map((err) => err.message).join(", "),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("common.error"),
          description:
            error instanceof Error ? error.message : t("message.create_failed"),
        });
      }
    },
  });

  const handleSubmit = async (data: MessageFormData) => {
    try {
      await createMessageMutation.mutateAsync(data);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const sendMessageMutation = useMutation({
    mutationKey: ["sendMessage"],
    mutationFn: async (data: MessageFormData) => {
      if (schedule) {
        throw new Error("Cannot send a scheduled message immediately");
      }
      data = await handleMediaUpload(data);
      const message = await apiService.createMessage(data);
      console.log(message);
      await apiService.sendMessage(message.data.id);
      return message;
    },
    onSuccess: () => {
      // invalidate the messages query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast({
        title: t("common.success"),
        description: t("message.sent_successfully"),
      });
      navigate("/dashboard");
    },
    onError: (error) => {
      if (error instanceof ZodError) {
        toast({
          variant: "destructive",
          title: t("common.validation_error"),
          description: error.errors.map((err) => err.message).join(", "),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("common.error"),
          description:
            error instanceof Error ? error.message : t("message.send_failed"),
        });
      }
    },
  });

  const handleSend = async (data: MessageFormData) => {
    try {
      if (schedule) {
        // Schedules should not send immediately
        await createMessageMutation.mutateAsync(data);
      } else {
        await sendMessageMutation.mutateAsync(data);
      }
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="hidden sm:block mb-8">
        <h1 className="text-3xl font-bold">
          {t("pages.messages.create.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("pages.messages.create.subtitle")}
        </p>
      </div>

      <MessageForm
        onSubmit={handleSubmit}
        onSend={handleSend}
        isLoading={
          createMessageMutation.status === "pending" ||
          sendMessageMutation.status === "pending" ||
          isUploadingMedia
        }
        isScheduled={schedule !== undefined}
      >
        <div>
          <ScheduleInput date={schedule} setDate={setSchedule} />

          {isUploadingMedia && (
            <div className="mt-4 w-full bg-gray-300 rounded-md overflow-hidden">
              <div
                className={clsx(
                  "bg-primary h-8 flex items-center justify-center text-sm font-medium transition-all duration-300",
                  uploadProgress > 100 ? "text-white" : "text-black"
                )}
                style={{ width: `${uploadProgress}%` }}
              >
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>
      </MessageForm>
    </div>
  );
};

export default CreateMessage;
